import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { readConsent, saveConsent, OPEN_PREFS_EVENT } from "@/lib/consent";

const Preferences = ({ open, onOpenChange, onSave }) => {
  const [analytics, setAnalytics] = useState(() => readConsent()?.analytics === true);
  useEffect(() => {
    if (open) setAnalytics(readConsent()?.analytics === true);
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="consent-preferences-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Gérer mes préférences</DialogTitle>
          <DialogDescription>Vous pouvez modifier ou retirer votre choix à tout moment depuis la page Cookies et stockage local.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
            <div>
              <p className="font-semibold">Strictement nécessaires</p>
              <p className="mt-1 text-muted-foreground">Connexion, sécurité, Workplace actif, préférences d'affichage et mode hors ligne. Toujours actifs.</p>
            </div>
            <Switch checked disabled aria-label="Strictement nécessaires" data-testid="consent-necessary-switch" />
          </div>
          <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
            <div>
              <p className="font-semibold">Mesure d'audience de la plateforme Emergent</p>
              <p className="mt-1 text-muted-foreground">Statistiques de fréquentation et de fiabilité, y compris l'enregistrement de session. Désactivée tant que vous ne l'acceptez pas.</p>
            </div>
            <Switch checked={analytics} onCheckedChange={setAnalytics} aria-label="Mesure d'audience" data-testid="consent-analytics-switch" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onSave(false)} data-testid="consent-prefs-refuse-all">Tout refuser</Button>
          <Button variant="outline" onClick={() => onSave(true)} data-testid="consent-prefs-accept-all">Tout accepter</Button>
          <Button onClick={() => onSave(analytics)} data-testid="consent-prefs-save">Enregistrer mon choix</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ConsentBanner = () => {
  const [show, setShow] = useState(() => !readConsent());
  const [prefs, setPrefs] = useState(false);
  useEffect(() => {
    const open = () => setPrefs(true);
    window.addEventListener(OPEN_PREFS_EVENT, open);
    return () => { window.removeEventListener(OPEN_PREFS_EVENT, open); };
  }, []);
  const choose = (analytics) => { saveConsent(analytics); setShow(false); setPrefs(false); };
  return (
    <>
      {show && <div aria-hidden className="h-80 sm:h-40" data-testid="consent-banner-spacer" />}
      {show && (
        <div role="region" aria-label="Choix des traceurs" data-testid="consent-banner"
          className="fixed inset-x-3 bottom-3 z-[60] mx-auto max-w-3xl rounded-xl border border-border bg-card p-4 shadow-2xl sm:p-5">
          <p className="text-sm">
            Avec votre accord, Safe Start active la mesure d'audience fournie par sa plateforme d'hébergement Emergent (statistiques de fréquentation et enregistrement de session) pour améliorer la fiabilité du service. Refuser n'empêche pas d'utiliser Safe Start.{" "}
            <Link to="/infos/cookies" className="underline" data-testid="consent-learn-more">En savoir plus</Link>
          </p>
          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Button variant="outline" onClick={() => choose(false)} data-testid="consent-refuse-all">Tout refuser</Button>
            <Button variant="outline" onClick={() => setPrefs(true)} data-testid="consent-customize">Personnaliser</Button>
            <Button variant="outline" onClick={() => choose(true)} data-testid="consent-accept-all">Tout accepter</Button>
          </div>
        </div>
      )}
      <Preferences open={prefs} onOpenChange={setPrefs} onSave={choose} />
    </>
  );
};
